import { Component, EventEmitter, OnInit, Output, ViewChild, Inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { GridDataResult, RowClassArgs } from '@progress/kendo-angular-grid';
import { NotificationService } from '@progress/kendo-angular-notification';
import { State, process, filterBy, CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { Guid } from 'guid-typescript';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable } from 'rxjs';
import { map } from 'rxjs';
import { PerformanceBodStoreService } from 'src/app/services/performance-bod-store.service';
import { InfrastructureCommercialService } from './infrastructure-commercial.service';
import { InfrastructureCommercial } from 'src/app/api/infrastructure-commercial';
import { IPerformanceBodPads } from 'src/app/api/performance-bod-pads';
import { IPerformanceBodPackages } from 'src/app/api/performance-bod-package';
import { UtilService } from 'src/app/services/util.service';
import { PermissionsProvider } from 'src/app/services/permissions.provider';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { DragAndDropService } from 'src/app/_shared/drag-and-drop.service';

const DRAGGABLE_SELECTOR = '.infrastructure-commercial-draggable';

@Component({
    selector: 'app-infrastructure-commercial',
    templateUrl: './infrastructure-commercial.component.html',
    standalone: false
})
export class InfrastructureCommercialComponent implements OnInit {
  public filter: CompositeFilterDescriptor;
  public filterable: boolean = true;
  public warningMessage: string =
    'Please provide a Well Pad Name on the Package Information Section (Performance BOD Summary Tab) before adding a new record';
  public countWellPadName: number = 0;
  public openDialog: boolean = false;
  public messageDuplicate: boolean = true;
  public view: Observable<GridDataResult>;
  public performanceBodPackagesList: IPerformanceBodPackages[];
  public performanceBodPadsList: IPerformanceBodPads[];
  public performanceBodId: string;
  public padNames: string[] = [];
  public formGroup: UntypedFormGroup;
  public gridState: State = {
    sort: [],
    skip: 0,
  };
  public canEditPerformanceBod: boolean = false;
  public infrastructureCommercialService: InfrastructureCommercialService;
  @Output() public performanceBodUpdate = new EventEmitter<string>();
  @Output() public commercialCreate = new EventEmitter<any>();
  @Output() public saveBodCompleted = new EventEmitter<boolean>();
  @ViewChild('infrastructureCommercial')
  private infrastructureCommercialGrid: any;
  private dragAndDropService: DragAndDropService;

  constructor(
    private performanceBodStoreService: PerformanceBodStoreService,    
    private formBuilder: UntypedFormBuilder,
    private spinner: NgxSpinnerService,
    private notificationService: NotificationService,
    private utilService: UtilService,
    private permissionsProvider: PermissionsProvider,
    private authenticationService: AuthenticationService,
    @Inject(DragAndDropService) dragAndDropServiceFactory: any,
    @Inject(InfrastructureCommercialService) infrastructureCommercialServiceFactory: any
  ) {
    this.dragAndDropService = dragAndDropServiceFactory();
    this.infrastructureCommercialService = infrastructureCommercialServiceFactory();
  }

  public ngOnInit() {
    this.canEditPerformanceBod = this.permissionsProvider.canEditPerformanceBod;
    this.performanceBodStoreService.performanceBodId$.subscribe(id => {
      this.performanceBodId = id;
      this.infrastructureCommercialService.performanceBodId = id;
      this.infrastructureCommercialService.read();
    });

    this.performanceBodStoreService.performanceBodPackagesData$.subscribe(data => {
      this.performanceBodPackagesList = data;
    });

    this.performanceBodStoreService.performanceBodPadsData$.subscribe(data => {
      this.performanceBodPadsList = data.filter(row => row.wellPadName !== '');
    });

    this.view = this.infrastructureCommercialService.pipe(
      map(data => {
        this.dragAndDropService.dataSourceRefreshed(data);
        return process(filterBy(data, this.filter), this.gridState);
      })
    );    

    this.dragAndDropService.initialize(this.infrastructureCommercialService.data, DRAGGABLE_SELECTOR, () => {
      this.infrastructureCommercialService.markAsSetOrder(this.authenticationService.getCurrentUser());
    });
  }

  public ngAfterViewInit(): void {
    // To Do
  }

  public ngOnDestroy(): void {
    this.dragAndDropService.refreshDragAndDrop();
  }

  public createFormGroup(dataItem: any): UntypedFormGroup {
    return this.formBuilder.group({
      performanceBodPadsId: new UntypedFormControl(dataItem.performanceBodPadsId, [Validators.required]),
      performanceBodPackagesId: new UntypedFormControl(dataItem.performanceBodPackagesId),
      description: new UntypedFormControl(dataItem.description, [Validators.required, Validators.maxLength(250)]),
      otherComposition: new UntypedFormControl(dataItem.otherComposition, [
        Validators.minLength(0),
        Validators.maxLength(250),
      ]),
      tieInLocation: new UntypedFormControl(dataItem.tieInLocation, [Validators.minLength(0), Validators.maxLength(250)]),
      comments: new UntypedFormControl(dataItem.comments, [Validators.minLength(0), Validators.maxLength(500)]),
      capacity: new UntypedFormControl(dataItem.capacity, [Validators.min(0), Validators.max(1000000)]),
      newPlusExistingPeakProduction: new UntypedFormControl(dataItem.newPlusExistingPeakProduction, [
        Validators.min(0),
        Validators.max(1000000),
      ]),
      maxPressurePsig: new UntypedFormControl(dataItem.maxPressurePsig, [Validators.min(0), Validators.max(100000)]),
      operatingPressurePsig: new UntypedFormControl(dataItem.operatingPressurePsig, [
        Validators.min(0),
        Validators.max(100000),
      ]),
      readyForServiceDate: new UntypedFormControl(dataItem.readyForServiceDate, [Validators.required]),
      isDeleted: new UntypedFormControl(dataItem.isDeleted, [Validators.required]),
      createdBy: new UntypedFormControl(dataItem.createdBy),
      createdDate: new UntypedFormControl(dataItem.createdDate),
      updatedBy: new UntypedFormControl(dataItem.updatedBy),
      updatedDate: new UntypedFormControl(dataItem.updatedDate),
    });
  }

  public cellClickHandler({ sender, rowIndex, columnIndex, dataItem, isEdited }) {
    if (!isEdited && this.canEditPerformanceBod) {
      sender.editCell(rowIndex, columnIndex, this.createFormGroup(dataItem));
      this.dragAndDropService.onEditingMode();
    }
  }

  public cellCloseHandler(args: any) {
    const { formGroup, dataItem, column, rowIndex } = args;

    let columnField = '';

    if (column.field === 'performanceBodPadsName') {
      columnField = 'performanceBodPadsId';
    }
    if (column.field === 'performanceBodPackagesName') {
      columnField = 'performanceBodPackagesId';
    } else {
      columnField = column.field;
    }

    const checkColumn = formGroup.controls[columnField];

    if (!checkColumn.valid) {
      args.preventDefault();
    } else if (checkColumn.dirty) {
      if (columnField === 'performanceBodPadsId') {
        dataItem.performanceBodPadsName = this.getPerformanceBodPads(formGroup.controls[columnField].value);
      }
      if (columnField === 'performanceBodPackagesId') {
        dataItem.performanceBodPackagesName = this.getPerformanceBodPackages(formGroup.controls[columnField].value);
      }
    }

    if (!formGroup.valid) {
      // prevent closing the edited cell if there are invalid values.
      args.preventDefault();
    } else if (formGroup.dirty) {
      if (formGroup.valid && args.sender.data.total > 0) {
        for (let i = 0; i <= args.sender.data.total - 1; i++) {
          if (rowIndex != i) {
            if (
              args.sender.data.data[i].performanceBodPadsId === formGroup.value.performanceBodPadsId &&
              args.sender.data.data[i].performanceBodPackagesId === formGroup.value.performanceBodPackagesId &&
              args.sender.data.data[i].description.trim() === formGroup.value.description.trim() &&
              args.sender.data.data[i].id !== formGroup.value.id
            ) {
              this.messageDuplicate = false;
              args.preventDefault();
              return;
            } else {
              this.messageDuplicate = true;
            }
          }
        }          
      }

      if (this.messageDuplicate) {
        dataItem.updatedDate = new Date();
        // dataItem.updatedBy = this.currentUser.cai;

        // Date is not working in Object.assign(), so need to be do it manually
        formGroup.value.updatedBy = dataItem.updatedBy;
        formGroup.value.updatedDate = dataItem.updatedDate;

        this.infrastructureCommercialService.assignValues(dataItem, formGroup.value);
        this.infrastructureCommercialService.updateItem(dataItem);
      }
    }
    // calls this to add the attribute of current row
    this.dragAndDropService.onEditingClose();
    this.dragAndDropService.refreshDragAndDrop();
  }

  public addHandler({ sender, rowIndex }) {
    const item: InfrastructureCommercial = {
      id: null,
      performanceBodPadsId: '',
      performanceBodPackagesId: '',
      description: null,
      otherComposition: null,
      tieInLocation: null,
      comments: null,
      capacity: 0.0,
      newPlusExistingPeakProduction: 0.0,
      maxPressurePsig: 0.0,
      operatingPressurePsig: 0.0,
      readyForServiceDate: null,
      isDeleted: false,
      createdBy: this.authenticationService.getCurrentUser(),
      createdDate: new Date(),
      updatedBy: this.authenticationService.getCurrentUser(),
      updatedDate: new Date(),
    };

    if (this.performanceBodPadsList) {
      for (const pad of this.performanceBodPadsList) {
        if (pad.wellPadName === '') {
          this.countWellPadName = this.countWellPadName + 1;
        }
      }
      if (this.countWellPadName === this.performanceBodPadsList.length) {
        this.openDialog = true;
        this.cancelHandler({ sender, rowIndex });
        this.countWellPadName = 0;
        return;
      } else {
        item.createdBy = this.authenticationService.getCurrentUser();
        item.createdDate = new Date();
        sender.addRow(this.createFormGroup(item));
        this.dragAndDropService.onEditingMode();
      }
    }
  }

  public cancelHandler({ sender, rowIndex }) {
    this.messageDuplicate = true;
    sender.closeRow(rowIndex);
    this.dragAndDropService.onEditingClose();
    this.dragAndDropService.refreshDragAndDrop();
  }

  public saveHandler({ sender, formGroup, rowIndex }) {
    if (formGroup.valid && sender.data.total > 0) {
      for (let i = 0; i <= sender.data.total - 1; i++) {
        if (
          sender.data.data[i].performanceBodPadsId === formGroup.value.performanceBodPadsId &&
          sender.data.data[i].performanceBodPackagesId === formGroup.value.performanceBodPackagesId &&
          sender.data.data[i].description.trim() === formGroup.value.description.trim() &&
          sender.data.data[i].id !== formGroup.value.id
        ) {
          this.messageDuplicate = false;
          return;
        } else {
          this.messageDuplicate = true;
        }
      }
    }

    if (this.messageDuplicate) {
      if (formGroup.valid) {
        if (this.performanceBodPadsList) {
          for (const pad of this.performanceBodPadsList) {
            if (pad.wellPadName === '') {
              this.countWellPadName = this.countWellPadName + 1;
            }
          }
          if (this.countWellPadName === this.performanceBodPadsList.length) {
            this.openDialog = true;
            this.countWellPadName = 0;
            return;
          } else {
            formGroup.value.id = Guid.create().toString();
            this.infrastructureCommercialService.createItem(formGroup.value);
            sender.closeRow(rowIndex);
            this.dragAndDropService.onEditingClose();
            this.dragAndDropService.dataSourceRefreshed(this.infrastructureCommercialService.data);
            this.dragAndDropService.refreshDragAndDrop();
            this.infrastructureCommercialService.markAsSetOrder(this.permissionsProvider.cai);
          }
        }
      } else {
        this.utilService.markFormGroupAsTouched(formGroup);
      }
    }
  }

  public removeHandler({ sender, dataItem }) {
    dataItem.isDeleted = true;
    this.infrastructureCommercialService.removeItem(dataItem);

    sender.cancelCell();
    
  }

  public saveChanges(grid: any): void {
    grid.closeCell();
    grid.cancelCell();
    this.spinner.show();
    // calls this to add the attribute of current row
    this.dragAndDropService.refreshDragAndDrop();
    this.infrastructureCommercialService.saveChanges();
    this.performanceBodUpdate.next(this.authenticationService.getCurrentUser());

    setTimeout(() => {
      this.spinner.hide();
      this.notificationService.show({
        content: 'Infrastructure - Commercial Information saved',
        position: { horizontal: 'center', vertical: 'top' },
        animation: { type: 'fade', duration: 500 },
        type: { style: 'success', icon: true },
        hideAfter: 700,
      });
      // Important to refresh grid
      this.infrastructureCommercialService.cancelChanges();
    }, 3000);
  }

  public saveFromBOD(): void {
    this.infrastructureCommercialGrid.closeCell();
    this.infrastructureCommercialGrid.cancelCell();

    if (this.infrastructureCommercialService.hasChanges()) {
      this.spinner.show();
      // calls this to add the attribute of current row
      this.dragAndDropService.refreshDragAndDrop();
      this.infrastructureCommercialService.saveChanges();
      this.performanceBodUpdate.next(this.authenticationService.getCurrentUser());

      const subscription = this.infrastructureCommercialService.subscribe(() => {
        this.saveBodCompleted.next(true);
      });

      subscription.unsubscribe();
    }
  }

  public cancelChanges(grid: any): void {
    this.messageDuplicate = true;
    grid.cancelCell();
    this.dragAndDropService.refreshDragAndDrop();
    this.infrastructureCommercialService.cancelChanges();
  }

  public onStateChange(state: State) {
    this.gridState = state;
    this.infrastructureCommercialService.read();

    
  }

  public getPerformanceBodPackages(id: string): any {
    if (this.performanceBodPackagesList === null || this.performanceBodPackagesList === undefined) {
      return '';
    }
    return this.performanceBodPackagesList.find(x => x.id === id);
  }

  public changePackage(value, dataItem: InfrastructureCommercial, formGroup: UntypedFormGroup) {
    const performanceBodPad = this.performanceBodPadsList.find(s => s.id === value);
    let performanceBodPackagesId: string;
    dataItem.performanceBodPackagesId = performanceBodPad ? performanceBodPad.performanceBodPackagesId : null;
    performanceBodPackagesId = dataItem.performanceBodPackagesId;
    formGroup.patchValue({
      performanceBodPackagesId,
    });

    if (!dataItem.id) {
      dataItem.performanceBodPadsId = performanceBodPad ? performanceBodPad.id : null;
      this.commercialCreate.next(dataItem);
      formGroup.patchValue({
        readyForServiceDate: dataItem.readyForServiceDate,
      });
    }
  }

  public getPerformanceBodPads(id: string): any {
    if (this.performanceBodPadsList === null || this.performanceBodPadsList === undefined) {
      return '';
    } else {
      return this.performanceBodPadsList.find(x => x.id === id);
    }
  }

  public closeKendoPopUp() {
    this.openDialog = false;
  }

  public handlerFilter(filter: CompositeFilterDescriptor): void {
    this.filter = filter;
    if (filter.filters) {
      if (filter.filters.length > 0) {
        this.dragAndDropService.onEditingMode();
      } else {
        this.dragAndDropService.onEditingClose();
        this.dragAndDropService.refreshDragAndDrop();
      }
    }
  }

  public rowCallback(context: RowClassArgs) {
    return {
      dragging: context.dataItem.dragging,
    };
  }

  public rowLoaded(): void {
    // to check until grid have been loeaded.
    this.dragAndDropService.checkIsOnEditMode();
    this.dragAndDropService.checkDraggableElementLoaded();
  }
}
